import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, LoginCredentials, MembershipView } from '@/types/auth.types'
import apiClient from '@/services/api/client'

// Session timeout: 24 hours (in milliseconds)
const SESSION_TIMEOUT = 24 * 60 * 60 * 1000 // 24 hours

const LS_MEMBERSHIPS = 'acc:memberships'
const LS_ACTIVE_TENANT = 'acc:activeTenantId'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const isInitialized = ref(false)

  // Sprint 1.6 — multi-tenant context
  const memberships = ref<MembershipView[]>([])
  const activeTenantId = ref<number | null>(null)
  const permissions = ref<string[]>([])
  const permissionsLoaded = ref(false)
  const switchingTenant = ref(false)

  // Load token + workspace state from localStorage on store creation
  try {
    const storedToken = localStorage.getItem('token')
    if (storedToken) token.value = storedToken
    const storedMemberships = localStorage.getItem(LS_MEMBERSHIPS)
    if (storedMemberships) memberships.value = JSON.parse(storedMemberships)
    const storedActive = localStorage.getItem(LS_ACTIVE_TENANT)
    if (storedActive) activeTenantId.value = Number(storedActive)
  } catch {
    // localStorage không khả dụng (private mode, iframe...)
  }

  // Simple authentication check - don't check timeout here (computed should be pure)
  const isAuthenticated = computed(() => {
    return !!(token.value && user.value)
  })
  
  const userRole = computed(() => user.value?.role || null)
  const activeMembership = computed(
    () => memberships.value.find((m) => m.tenantId === activeTenantId.value) || null,
  )
  const activeTenantName = computed(() => activeMembership.value?.tenantName || '')
  const activeTier = computed(() => activeMembership.value?.tier || 'user')

  function updateLastActivity() {
    if (token.value) {
      try {
        localStorage.setItem('lastActivity', Date.now().toString())
      } catch { /* ignore */ }
    }
  }

  function isSessionValid(): boolean {
    if (!token.value) return false
    try {
      const lastActivity = localStorage.getItem('lastActivity')
      if (!lastActivity) return true
      const timeSinceLastActivity = Date.now() - parseInt(lastActivity, 10)
      return timeSinceLastActivity <= SESSION_TIMEOUT
    } catch {
      return true
    }
  }

  // Check session timeout and logout if expired
  async function checkSessionTimeout(): Promise<boolean> {
    if (!token.value) return false
    
    if (!isSessionValid()) {
      // Session expired
      await logout()
      return false
    }
    
    // Update activity on successful check
    updateLastActivity()
    return true
  }

  async function login(credentials: LoginCredentials) {
    if (loading.value) return
    loading.value = true
    error.value = null
    try {
      const response = await apiClient.post('/auth/login', credentials)
      const { token: newToken, user: userData, memberships: m, activeTenantId: tid } =
        response.data.data

      token.value = newToken
      user.value = userData
      memberships.value = m || []
      activeTenantId.value = tid ?? null

      try {
        localStorage.setItem('token', newToken)
        localStorage.setItem(LS_MEMBERSHIPS, JSON.stringify(memberships.value))
        if (activeTenantId.value !== null) {
          localStorage.setItem(LS_ACTIVE_TENANT, String(activeTenantId.value))
        }
      } catch { /* ignore */ }
      updateLastActivity()

      // Fetch effective permissions từ IAM
      await fetchPermissions()

      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Đăng nhập thất bại'
      token.value = null
      user.value = null
      memberships.value = []
      activeTenantId.value = null
      permissions.value = []
      permissionsLoaded.value = false
      try {
        localStorage.removeItem('token')
        localStorage.removeItem('lastActivity')
        localStorage.removeItem(LS_MEMBERSHIPS)
        localStorage.removeItem(LS_ACTIVE_TENANT)
      } catch { /* ignore */ }
      throw err
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      await apiClient.post('/auth/logout')
    } catch (err) {
      // Ignore errors on logout
    } finally {
      token.value = null
      user.value = null
      memberships.value = []
      activeTenantId.value = null
      permissions.value = []
      permissionsLoaded.value = false
      try {
        localStorage.removeItem('token')
        localStorage.removeItem('lastActivity')
        localStorage.removeItem(LS_MEMBERSHIPS)
        localStorage.removeItem(LS_ACTIVE_TENANT)
      } catch { /* ignore */ }
    }
  }

  async function fetchCurrentUser() {
    try {
      const response = await apiClient.get('/auth/me')
      user.value = response.data.data
      return response.data.data
    } catch (err) {
      await logout()
      throw err
    }
  }

  async function fetchPermissions(force = false) {
    if (permissionsLoaded.value && !force) return permissions.value
    // Không có tenant active → trả [] nhưng KHÔNG mark loaded để có thể retry khi switchTenant xong
    if (!activeTenantId.value) {
      permissions.value = []
      return []
    }
    try {
      const res = await apiClient.get('/iam/me/permissions')
      permissions.value = res.data?.data?.permissions || []
    } catch {
      permissions.value = []
    } finally {
      permissionsLoaded.value = true
    }
    return permissions.value
  }

  async function switchTenant(tenantId: number) {
    if (switchingTenant.value) return
    switchingTenant.value = true
    try {
      const res = await apiClient.post('/auth/switch-tenant', { tenantId })
      const { token: newToken, activeTenantId: newTid } = res.data.data
      token.value = newToken
      activeTenantId.value = newTid
      try {
        localStorage.setItem('token', newToken)
        localStorage.setItem(LS_ACTIVE_TENANT, String(newTid))
      } catch { /* ignore */ }
      // Re-fetch user (server cập nhật JWT có tenant_id mới ảnh hưởng các filter) + permissions
      await fetchCurrentUser()
      permissionsLoaded.value = false
      await fetchPermissions(true)
    } catch (err) {
      throw err
    } finally {
      switchingTenant.value = false
    }
  }

  // Init: Fetch user nếu có token khi app khởi động
  async function init() {
    if (isInitialized.value) return // Already initialized
    
    if (!token.value) {
      isInitialized.value = true
      return // No token, nothing to do
    }
    
    // Check session validity first (don't logout yet, just check)
    if (!isSessionValid()) {
      // Session expired, clear everything
      await logout()
      isInitialized.value = true
      return
    }
    
    // Token exists and session is valid, fetch user + permissions
    try {
      await fetchCurrentUser()
      updateLastActivity()
      // Sprint 1.6 — load effective permissions từ IAM (memberships đã restore từ localStorage)
      await fetchPermissions()
    } catch (err: any) {
      // Token invalid or expired, clear it
      console.error('Failed to fetch current user:', err)
      await logout()
    } finally {
      isInitialized.value = true
    }
  }

  return {
    user,
    token,
    loading,
    error,
    isAuthenticated,
    userRole,
    isInitialized,
    memberships,
    activeTenantId,
    activeMembership,
    activeTenantName,
    activeTier,
    permissions,
    permissionsLoaded,
    switchingTenant,
    login,
    logout,
    fetchCurrentUser,
    fetchPermissions,
    switchTenant,
    init,
    updateLastActivity,
    checkSessionTimeout,
    isSessionValid,
  }
})

